import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { ArrowRight, ShoppingBasket, X } from "lucide-react";
import { useApp } from "../context/AppContext";
import { formatRupiah } from "../lib/format";
import { useIsDesktop } from "../hooks/useIsDesktop";
import CartItemRow from "./CartItemRow";
import BrandLogo from "./BrandLogo";

/**
 * Unified cart view built with plain Tailwind transitions (no Radix Sheet)
 * to guarantee smooth slide behaviour on all browsers.
 * - Mobile: slides up from bottom (rounded top, max-h 88vh)
 * - Desktop: slides in from right (~420px wide, full height)
 */
const CartSheet = () => {
  const {
    cartItems,
    cartSubtotal,
    cartTotal,
    cartOpen,
    closeCart,
    setCartQty,
    removeFromCart,
    selectedCourier,
  } = useApp();
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const location = useLocation();

  // Prevent body scroll when open
  useEffect(() => {
    if (cartOpen) {
      const orig = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = orig;
      };
    }
  }, [cartOpen]);

  // Close on Escape
  useEffect(() => {
    if (!cartOpen) return;
    const onKey = (e) => e.key === "Escape" && closeCart();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cartOpen, closeCart]);

  const goCheckout = () => {
    closeCart();
    if (
      location.pathname === "/checkout/pembayaran" ||
      location.pathname === "/checkout/pengiriman"
    ) {
      return;
    }
    navigate("/checkout/pengiriman");
  };

  const panelBase =
    "fixed z-50 bg-white shadow-2xl flex flex-col will-change-transform transition-transform duration-300 ease-out";
  const panelPlacement = isDesktop
    ? "top-0 right-0 h-full w-full sm:w-[420px]"
    : "left-0 right-0 bottom-0 max-h-[88vh] rounded-t-3xl";
  const panelTransform = cartOpen
    ? "translate-x-0 translate-y-0"
    : isDesktop
    ? "translate-x-full"
    : "translate-y-full";

  return (
    <>
      {/* Overlay */}
      <div
        onClick={closeCart}
        aria-hidden={!cartOpen}
        className={`fixed inset-0 z-40 bg-stone-900/40 backdrop-blur-sm transition-opacity duration-300 ${
          cartOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        data-testid="cart-overlay"
      />

      {/* Panel */}
      <aside
        className={`${panelBase} ${panelPlacement} ${panelTransform}`}
        role="dialog"
        aria-modal="true"
        aria-label="Keranjang"
        data-testid="cart-sheet"
        data-open={cartOpen ? "true" : "false"}
      >
        {/* Drag indicator on mobile */}
        {!isDesktop && (
          <div className="flex justify-center pt-3 pb-1 shrink-0">
            <span className="w-10 h-1.5 rounded-full bg-stone-300"></span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 pt-4 pb-3 border-b border-stone-100 shrink-0">
          <h2 className="font-display text-xl sm:text-2xl font-semibold text-stone-900 flex items-center gap-2">
            <BrandLogo className="h-8 w-8" padded rounded="rounded-lg" />
            Keranjang Anda
          </h2>
          <button
            type="button"
            onClick={closeCart}
            className="w-10 h-10 rounded-xl text-stone-500 hover:bg-stone-100 flex items-center justify-center"
            aria-label="Tutup"
            data-testid="cart-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        {cartItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8 py-12">
            <span className="inline-flex w-16 h-16 rounded-2xl bg-stone-100 text-stone-400 items-center justify-center mb-4">
              <ShoppingBasket className="w-7 h-7" />
            </span>
            <p className="font-display text-lg font-semibold text-stone-900">
              Keranjang masih kosong
            </p>
            <p className="text-stone-500 mt-1 max-w-xs">
              Pilih produk herbal favorit Anda untuk mulai berbelanja.
            </p>
            <Button
              onClick={closeCart}
              className="mt-6 h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-6"
              data-testid="cart-empty-close"
            >
              Lihat Produk
            </Button>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-2">
            <ul className="divide-y divide-stone-100">
              {cartItems.map((item) => (
                <CartItemRow
                  key={item.productId}
                  item={item}
                  onDec={() => setCartQty(item.productId, item.quantity - 1)}
                  onInc={() =>
                    setCartQty(
                      item.productId,
                      Math.min(item.stock, item.quantity + 1)
                    )
                  }
                  onRemove={() => removeFromCart(item.productId)}
                />
              ))}
            </ul>
          </div>
        )}

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="shrink-0 border-t border-stone-100 px-5 sm:px-6 py-4 space-y-3 bg-stone-50/60">
            <div className="flex justify-between text-stone-700">
              <span>Subtotal</span>
              <span className="font-medium text-stone-900">
                {formatRupiah(cartSubtotal)}
              </span>
            </div>
            {selectedCourier && (
              <div className="flex justify-between text-stone-700">
                <span>Ongkir ({selectedCourier.name})</span>
                <span className="font-medium text-stone-900">
                  {formatRupiah(selectedCourier.cost)}
                </span>
              </div>
            )}
            <div className="flex justify-between items-baseline pt-2 border-t border-stone-200">
              <span className="font-display text-base font-medium text-stone-900">
                Total Bayar
              </span>
              <span
                className="font-display text-2xl font-semibold text-emerald-700"
                data-testid="cart-sheet-total"
              >
                {formatRupiah(cartTotal > 0 ? cartTotal : cartSubtotal)}
              </span>
            </div>
            <Button
              onClick={goCheckout}
              className="w-full h-14 rounded-xl text-base bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="cart-sheet-checkout"
            >
              Lanjut Isi Data Pengiriman
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}
      </aside>
    </>
  );
};

export default CartSheet;
